import { useEffect, useRef, useState } from 'react';
import { refreshBankSuggestions } from '../services/MatchingService';

function snapshot(line) {
    try { return typeof line.suggestion_snapshot === 'string' ? JSON.parse(line.suggestion_snapshot) : line.suggestion_snapshot; }
    catch (_) { return null; }
}

export default function useBankSuggestionRefresh({ companyId, statements, isLoading, onRefreshed }) {
    const attempted = useRef(new Set());
    const running = useRef(false);
    const scope = useRef(companyId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [retry, setRetry] = useState(0);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (scope.current !== companyId) {
            scope.current = companyId;
            attempted.current.clear();
            setError(null);
        }
        if (!companyId || isLoading || running.current) return;
        const batch = statements.filter(line => {
            const saved = snapshot(line);
            const key = `${companyId}:${line.id}:${saved?.revision || saved?.computed_at || 'new'}`;
            return line.reconciliation_status === 'unmatched' && (!saved || saved.stale === true || !saved.engine_version)
                && !attempted.current.has(key);
        }).slice(0, 25);
        if (!batch.length) return;
        batch.forEach(line => {
            const saved = snapshot(line);
            attempted.current.add(`${companyId}:${line.id}:${saved?.revision || saved?.computed_at || 'new'}`);
        });
        running.current = true;
        setBusy(true);
        refreshBankSuggestions(companyId, batch.map(line => line.id))
            .then(async data => {
                if (!mounted.current || scope.current !== companyId) return;
                if (data?.result?.summary?.failed > 0) setError('Vissa förslag kunde inte uppdateras.');
                await onRefreshed();
            })
            .catch(() => {
                if (mounted.current && scope.current === companyId) setError('Kunde inte uppdatera bokföringsförslagen.');
            })
            .finally(() => {
                running.current = false;
                if (mounted.current) setBusy(false);
            });
    }, [companyId, statements, isLoading, onRefreshed, busy, retry]);
    return { busy, error, retry: () => { attempted.current.clear(); setError(null); setRetry(value => value + 1); } };
}
